import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import Chip, { ChipProps } from '@mui/material/Chip';
import { CandidateStatus, Status, StatusType } from 'generated-graphql/types';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { statusSetAtom } from 'services/candidate/atoms';
import {
  ArrowForwardOutlined,
  HandshakeOutlined,
  WorkHistoryOutlined
} from '@mui/icons-material';
import HandshakeIcon from '@mui/icons-material/Handshake';

const getStatusProps = (
  status: Status
): {
  color: 'primary' | 'info' | 'success' | 'error';
  icon: JSX.Element;
} => {
  switch (status.type) {
    case StatusType.Failed:
      return {
        color: 'error',
        icon: <CloseIcon fontSize="small" />
      };
    case StatusType.Interview:
      return {
        color: 'info',
        icon: <WorkHistoryIcon fontSize="small" />
      };
    case StatusType.Passed:
      return {
        color: 'success',
        icon: <CheckIcon fontSize="small" />
      };
    default:
      return {
        color: 'primary',
        icon: <HandshakeIcon fontSize="small" />
      };
  }
};

const getDefaultStatusChipProps = (
  status: CandidateStatus
): ChipProps | null => {
  switch (status) {
    case CandidateStatus.ShortListed:
      return {
        label: 'Shortlisted',
        color: 'success',
        icon: <CheckIcon fontSize="small" />
      };
    case CandidateStatus.Rejected:
      return {
        label: 'Rejected',
        color: 'error',
        icon: <CloseIcon fontSize="small" />
      };
    case CandidateStatus.Review:
      return {
        label: 'Review',
        color: 'warning',
        icon: <VisibilityIcon fontSize="small" />
      };
    case CandidateStatus.InterviewStage_1:
      return {
        label: 'Stage 1',
        color: 'primary',
        sx: {
          color: 'customGrey.main'
        },
        icon: <WorkHistoryOutlined fontSize="small" />
      };
    case CandidateStatus.InterviewStage_2:
      return {
        label: 'Stage 2',
        color: 'primary',
        sx: {
          color: 'customGrey.main'
        },
        icon: <WorkHistoryIcon fontSize="small" />
      };
    case CandidateStatus.Offer:
      return {
        label: 'Offer',
        color: 'primary',
        icon: <HandshakeOutlined fontSize="small" />
      };
    case CandidateStatus.NotProgressing:
      return {
        label: 'Not Progressing',
        color: 'error',
        icon: <CloseIcon fontSize="small" />
      };
    case CandidateStatus.Progressing:
      return {
        label: 'Progressing',
        color: 'success',
        icon: <ArrowForwardOutlined fontSize="small" />
      };
    default:
      return null;
  }
};

type Props = {
  status?: CandidateStatus | string | null;
};

export const CandidateStatusChip: React.FC<Props> = ({ status }) => {
  const statusSet = useRecoilValue(statusSetAtom);

  if (!status) {
    return null;
  }

  const defaultProps = getDefaultStatusChipProps(status as CandidateStatus);
  if (defaultProps) {
    return <Chip {...defaultProps} />;
  }

  const correspondingStatusInSet = statusSet?.statuses.find(
    (s) => s.value === status
  );

  if (correspondingStatusInSet) {
    const { color, icon } = getStatusProps(correspondingStatusInSet);
    return (
      <Chip label={correspondingStatusInSet.label} color={color} icon={icon} />
    );
  }

  return null;
};
